/** Close a sprint, having said out loud what happens to the work still open in it.
 *
 * Two things used to go wrong, and this dialog exists for both.
 *
 * **A sprint could become uncloseable by clicking.** Starting a sprint demotes the
 * previously active one to `planned` rather than completing it, and the board only offered
 * "Complete sprint" while a sprint was active. So starting the next sprint stranded the
 * last one: still open, no button. The API had always accepted it; nothing offered it.
 * Now starting a sprint while another is open routes through here first.
 *
 * **Carry-over happened silently.** Unfinished work moved to the next sprint with nobody
 * asked and nothing shown, so the first anyone knew of it was finding last fortnight's
 * items on this fortnight's board. The choice is now explicit, and it defaults to the old
 * behaviour so nobody has to think about it on an ordinary Friday.
 *
 * What is deliberately *not* offered: leaving unfinished items on the completed sprint.
 * They would sit on a board nobody opens again. The question is where the work goes, never
 * whether it stays.
 */

import { useState } from 'react'

import { Button, ErrorState, Field, inputClass } from '@/components/ui'
import {
  type CarryOver,
  type Sprint,
  useCompleteSprint,
  useStartSprint,
} from '@/features/workbench/useWork'

export function CompleteSprintDialog({
  sprint,
  sprints,
  /** Started once the sprint is closed. Set when this was opened by "Start sprint". */
  thenStart,
  onClose,
}: {
  sprint: Sprint
  sprints: Sprint[]
  thenStart?: Sprint
  onClose: () => void
}) {
  const complete = useCompleteSprint()
  const start = useStartSprint()

  const openCount = sprint.openCount
  const doneCount = Math.max(sprint.itemCount - openCount, 0)

  // Where the work could go. A completed sprint would swallow it onto a closed board, and
  // a mirrored sprint belongs to Azure DevOps — the server refuses both, so neither is
  // offered. The sprint being closed is excluded for the obvious reason.
  const targets = sprints.filter(
    (candidate) =>
      candidate.id !== sprint.id && !candidate.isExternal && candidate.status !== 'completed',
  )

  // What the server picks when no target is named: the earliest *planned* sprint starting
  // no earlier than this one. Mirrors `_next_planned_sprint`, so the default option can
  // name the sprint it will actually use rather than describing it vaguely.
  // Sprints whose dates have already passed are skipped, as the server now skips them.
  const today = new Date().toISOString().slice(0, 10)
  const defaultTarget =
    targets
      .filter(
        (c) =>
          c.status === 'planned' &&
          c.startDate >= sprint.startDate &&
          c.endDate >= today &&
          // Unfinished work carries into the same team's next sprint, as on the server.
          (c.team ?? null) === (sprint.team ?? null),
      )
      .sort((a, b) => a.startDate.localeCompare(b.startDate))[0] ?? null

  const [carryOver, setCarryOver] = useState<CarryOver>('next_sprint')
  // If this was opened by starting another sprint, that sprint is where the team is about
  // to work — so it is the sensible destination, not whichever one sorts first.
  const [targetId, setTargetId] = useState<string>(
    thenStart && targets.some((t) => t.id === thenStart.id) ? thenStart.id : '',
  )

  const submit = () => {
    complete.mutate(
      {
        sprintId: sprint.id,
        carryOver,
        targetSprintId: carryOver === 'next_sprint' ? targetId || null : null,
      },
      {
        onSuccess: () => {
          // Completing first and starting second matters: while the old sprint is still
          // active the new one is not a legal carry-over target, so the server would
          // create a third sprint and put the work in that instead.
          if (thenStart) {
            start.mutate(thenStart.id, { onSuccess: onClose })
          } else {
            onClose()
          }
        },
      },
    )
  }

  const busy = complete.isPending || start.isPending
  const error = complete.error ?? start.error

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Complete ${sprint.name}`}
      >
        <h2 className="mb-1 font-heading text-2xl text-fg">Complete {sprint.name}</h2>
        <p className="mb-4 text-sm text-fg-muted">
          {thenStart ? (
            <>
              {sprint.name} is still open. Close it before starting {thenStart.name} — only
              one sprint runs at a time.
            </>
          ) : (
            <>{sprint.name} will be closed and moved to the completed sprints.</>
          )}
        </p>

        {error ? (
          <div className="mb-4">
            <ErrorState error={error} />
          </div>
        ) : null}

        <div className="card mb-4 p-3 text-sm">
          <div className="flex gap-6">
            <span>
              <strong className="text-fg">{doneCount}</strong>{' '}
              <span className="text-fg-muted">done</span>
            </span>
            <span>
              <strong className="text-fg">{openCount}</strong>{' '}
              <span className="text-fg-muted">still open</span>
            </span>
          </div>
          {openCount > 0 ? (
            <p className="mt-2 text-xs text-fg-muted">
              Open means anything not Done — to do, in progress and blocked.
            </p>
          ) : null}
        </div>

        {openCount > 0 ? (
          <div className="space-y-3">
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-fg">
                What should happen to the {openCount} open{' '}
                {openCount === 1 ? 'item' : 'items'}?
              </legend>

              <label className="mb-2 flex cursor-pointer gap-2 text-sm">
                <input
                  type="radio"
                  name="carryOver"
                  className="mt-0.5"
                  checked={carryOver === 'next_sprint'}
                  onChange={() => setCarryOver('next_sprint')}
                />
                <span>
                  <span className="text-fg">Carry them into another sprint</span>
                  <span className="block text-xs text-fg-muted">
                    They keep their assignee, status and history.
                  </span>
                </span>
              </label>

              <label className="flex cursor-pointer gap-2 text-sm">
                <input
                  type="radio"
                  name="carryOver"
                  className="mt-0.5"
                  checked={carryOver === 'backlog'}
                  onChange={() => setCarryOver('backlog')}
                />
                <span>
                  <span className="text-fg">Send them back to the backlog</span>
                  <span className="block text-xs text-fg-muted">
                    Use this when they should be re-planned rather than assumed.
                  </span>
                </span>
              </label>
            </fieldset>

            {carryOver === 'next_sprint' ? (
              <Field
                label="Carry into"
                hint={
                  defaultTarget
                    ? undefined
                    : 'There is no planned sprint to carry into, so the default creates one. Pick a sprint to send the work somewhere you named.'
                }
              >
                <select
                  className={inputClass}
                  value={targetId}
                  onChange={(event) => setTargetId(event.target.value)}
                >
                  {/* The default names the sprint the server will actually choose. It
                      considers only *planned* sprints, so when the next one has already
                      been started the default silently creates a third — the trap that
                      puts carried-over work in a sprint nobody named. Saying so is the fix. */}
                  <option value="">
                    {defaultTarget
                      ? `${defaultTarget.name} — the next planned sprint`
                      : 'A new sprint, created now'}
                  </option>
                  {targets.map((target) => (
                    <option key={target.id} value={target.id}>
                      {target.name}
                      {target.status === 'active' ? ' — currently active' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-fg-muted">
            Nothing is open, so nothing moves. This just closes the sprint.
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy
              ? 'Working…'
              : thenStart
                ? `Complete and start ${thenStart.name}`
                : 'Complete sprint'}
          </Button>
        </div>
      </div>
    </div>
  )
}
